import React, {FC, ReactElement, useState} from 'react';
import {ElementProps} from './Element';
import styled from 'styled-components';
import {ElementDesigned} from './Element';
import CopyToClip from './chart/helpers/CopyToClip';
import { chartSettings } from './chart/settings';

type ElementEditorProps = {
    element: any,
    settings: any,
    options: any,
    label: string,
}

type EditorProps = ElementProps & ElementEditorProps;


const ElementDesignedEditor = styled(ElementDesigned)`
&.elementWrapper {
      cursor:pointer;
      .element {
         &:not(.activeMode):hover {
          background: rgba(22, 118, 208, 0.05);
          outline: 0.5px solid #1676D0;
        }
      }
      iframe, video {
        pointer-events: none;
      }
      .separator {
        background: url(${props => props.separatorPlaceholder})
      }
}
`;

const ElementEditor: FC<EditorProps> = ({...props}): ReactElement => {
    const useAppSelector = props.editorPlugins.useAppSelector;
    const useAppDispatch = props.editorPlugins.useAppDispatch;
    const selectActive = props.editorPlugins.selectActive;
    const setActiveType = props.editorPlugins.setActiveType;
    const setActiveProps = props.editorPlugins.setActiveProps;
    const updateData = props.editorPlugins.updateData;
    const trackStory = props.editorPlugins.trackStory;
    const selectActiveSettings = props.editorPlugins.selectActiveSettings;
    const selectActiveSet = props.editorPlugins.selectActiveSet;
    const openElementsLibrary = props.editorPlugins.openElementsLibrary;
    const closeElementsLibrary = props.editorPlugins.closeElementsLibrary;
    const setSettingsTitle = props.editorPlugins.setSettingsTitle;
    const changeValue = props.editorPlugins.changeValue;
    const Duplicate = props.editorPlugins.duplicateButton;
    const Delete = props.editorPlugins.deleteButton;
    const Add = props.editorPlugins.addButton;
    const Draggable = props.editorPlugins.draggable;

    const activePath: any = useAppSelector((state: any) => state.editor.present.activePath);
    const globalData: any | null = useAppSelector((state: any) => state.editor.present.data);
    const environmentLink: any = useAppSelector((state: any) => state.editor.present.environment);
    const isStorydocAdmin: boolean = useAppSelector((state: any) => state.editor.present.isStorydocAdmin);
    const dispatch = useAppDispatch();
    const index = props.index ? props.index : 0;
    const Element = props.element;
    const elementData = {...props.elementData.data};
    elementData.name = props.elementData.name;
    const settings = getSettings();
    const separatorPlaceholder = environmentLink + '/assets/images/separatorPlaceholder.svg';
    const [ addElementTrigger, setAddElementTrigger ] = useState(false);

    if (props.elementData.data.isAddElement) {
        dispatch(trackStory({event: 'success add new element from elements library', screen: 'editorv4', params: {}}));
        let path = {...props.path};
        if (path.elementIndex === -1) path.elementIndex = 0;
        let changeValueData = {
            path: path,
            data: {
                name: 'isAddElement',
                content: false
            },
            object: 'element'
        };
        dispatch(changeValue(changeValueData));
        if (props.elementData.name === 'Image' || props.elementData.name === 'Video' || props.elementData.name === 'Icon') {
            let activeSetting;
            // if (props.elementData.name === 'Image') {
            //     activeSetting = 'Image';
            // }
            if (props.elementData.name === 'Video') {
                activeSetting = 'Video';
            }
            if (props.elementData.name === 'Icon') {
                activeSetting = 'Icon';
            }
            selectActiveElement(activeSetting);
        } else {
            selectActiveElement();
        }
    }


    function getSettings() {
        if (elementData.name === 'Chart') return chartSettings().settings[ elementData.chartType ];
        return props.settings;
    }

    function getLabel() {
        if (elementData.name === 'Chart') return chartSettings().labels[ elementData.chartType ];
        return props.label;
    }


    function handleClick(event: any) {
        setAddElementTrigger(false);
        event.preventDefault();
        return selectActiveElement();
    }

    function isElementActive() {
        if (!activePath) return false;
        let elementPath: any = props.path;
        if (!elementPath) return false;
        return JSON.stringify(activePath) === JSON.stringify(elementPath);
    }

    function isNextElementActive() {
        if (!activePath) return false;
        let elementPath: any = props.path;
        let path = {...elementPath};
        path.elementIndex = path.elementIndex + 1;
        if (!elementPath) return false;
        return JSON.stringify(activePath) === JSON.stringify(path);
    }


    function selectActiveElement(activeSetting: any = null) {
        dispatch(trackStory({event: 'click on element', screen: 'editorv4', params: {'editorVersion':'V4'}}));
        settings ? dispatch(selectActiveSettings({settings, elementData})) : dispatch(selectActiveSettings(null));
        dispatch(setSettingsTitle(props.elementData.name));
        if (activeSetting) setActiveSetting(activeSetting);
        dispatch(setActiveType('element'));
        let cleanProps = {...elementData};
        delete cleanProps.editorPlugins;
        dispatch(setActiveProps(cleanProps));
        dispatch(closeElementsLibrary());
        dispatch(selectActive(props.path));
        if (props.elementData.name === 'Image' && (!props.elementData.data.src || props.elementData.data.src && props.elementData.data.src.indexOf('imagePlaceholder') > -1)) handleEmptyImage();
    }

    function getActiveSetting(settings: any) {
        if (!settings) return;
        let activeSet: any;
        settings.map((group: any) => {
            if (activeSet) return;
            group.map((setting: any) => {
                if (activeSet) return;
                if (setting.isMainSettings) activeSet = setting.title || setting.settingName;
            });
        });
        return activeSet;
    }

    function setActiveSetting(activeSetting: any = null) {
        if (!activeSetting) activeSetting = getActiveSetting(settings);
        if (activeSetting) dispatch(selectActiveSet(activeSetting));
    }


    function handleEmptyImage() {
        document.dispatchEvent(new CustomEvent('settingsRenderAngular', {
            detail: {
                action: 'changeImage',
                path: props.path,
                object: 'element',
                property: 'src'
            }
        }));
    }

    function removeMainSetting() {
        dispatch(selectActiveSet(null));
    }

    function clickOpenElementsLibrary(top: boolean = false) {
        setAddElementTrigger(true);
        dispatch(trackStory({event: 'click add element', screen: 'editorv4', params: {'editorVersion':'V4'}}));
        let path = {...props.path};
        if (path.elementIndex == null || path.elementIndex === undefined) return;
        if(top) path.elementIndex = path.elementIndex - 1;
        let data = {
            path: path,
            type: 'element'
        };
        dispatch(selectActiveSet(''));
        dispatch(openElementsLibrary(data));
    }


    function duplicateElement(event: any, element: any) {
        event.stopPropagation();
        removeMainSetting();
        dispatch(trackStory({event: 'click duplicate element from main container', screen: 'editorv4', params: {}}));
        let elements = [...globalData.slides[activePath.slideIndex].combos[activePath.comboIndex].sets[activePath.setIndex].groups[activePath.groupIndex]];
        elements.splice(index + 1, 0, element);
        let data = {
            path: activePath,
            data: elements,
            object: 'element'
        };
        dispatch(updateData(data));
        dispatch(selectActive({...activePath, elementIndex: activePath.elementIndex + 1}));
    }

    function deleteElement(event: any) {
        event.stopPropagation();
        removeMainSetting();
        dispatch(trackStory({event: 'click delete element from main container', screen: 'editorv4', params: {}}));
        let elements = [...globalData.slides[activePath.slideIndex].combos[activePath.comboIndex].sets[activePath.setIndex].groups[activePath.groupIndex]];
        elements.splice(index, 1);
        let data = {
            path: activePath,
            data: elements,
            object: 'element'
        };
        dispatch(updateData(data));
        const elIndex = activePath.elementIndex === elements.length ? activePath.elementIndex - 1 : activePath.elementIndex;
        dispatch(selectActive({...activePath, elementIndex: elIndex}));
    }

    function openCrop() {
        removeMainSetting();
        document.dispatchEvent(new CustomEvent('settingsRenderAngular', {
            detail: {
                action: 'cropImage',
                path: activePath,
                src: props.elementData.data.src,
                object: 'element',
                cropData: props.elementData.data.cropData
            }
        }));
    }

    function openLink() {
        removeMainSetting();
        dispatch(selectActiveSet('Link'));
    }

    function openMediaLibrary(action: string = 'changeImage') {
        document.dispatchEvent(new CustomEvent('settingsRenderAngular', {
            detail: {
                action: action,
                path: activePath,
                object: 'element',
                property: 'src'
            }
        }));
    }

    function optionAction(event: any, option: any) {
        event.stopPropagation();
        dispatch(trackStory({event: 'click '+ option.name +' icon from active element buttons', screen: 'editorv4', params: {}}));
        switch (option.action) {
            case 'openCrop':
                return openCrop();
            case 'openLink':
                return openLink();
            case 'openMediaLibrary':
                return openMediaLibrary();
            case 'setActiveSetting':
            case 'editAnimatedList':
            case 'updateVideo':
            case 'setActiveWidth':
                return setActiveSetting(option.settingTitle);
        }
    }

    function checkIfDisableElement() {
        if(elementData.name === 'RichText' && isElementActive()) return true;
    }

    function handleDoubleClick() {
        if (elementData.name === 'Image') openMediaLibrary();
        if (elementData.name === 'Icon') setActiveSetting('Icon');
        if (elementData.name === 'Video') setActiveSetting('Video');
        if (elementData.name === 'Embedded') setActiveSetting('Embedded');
    }

    function elementContent() {
        return [
            props.path?.elementIndex === 0 && <div className={'addElement top'} onClick={() =>  clickOpenElementsLibrary(true)}><Add/></div>,
            <ElementDesignedEditor
                className={'elementWrapper ' + (isElementActive() ? 'active' : '')}
                separatorPlaceholder={separatorPlaceholder}
                horizontalAlignment={props.elementData.data.horizontalAlignment}
                onClick={(event) => handleClick(event)}
                onDoubleClick={handleDoubleClick}>
            <div className={'element ' + (isElementActive() && !addElementTrigger ? 'activeMode' : '')}>
                <Element {...elementData} editorPlugins={props.editorPlugins} path={props.path}/>
                <div className={'options '}>
                    <div className="left">
                        {
                            getLabel() && <div className="elementName">{getLabel()}</div>
                        }
                        {
                            props.options && <div className={'optionsWrapper'}>
                                {
                                    props.options.length && props.options.map((option: any, index:number) => {
                                        let passCondition = props.elementData.data[option.condition];
                                        if (!option.condition || (option.condition && passCondition)) {
                                            return (
                                                <div key={index} className={'option ' + option.name} onClick={(event) => optionAction(event, option)}><img src={option.icon} alt={option.title}/>{option.title}</div>
                                            );
                                        }
                                    })
                                }
                            </div>
                        }
                    </div>
                    <div className="right">
                        <div className={'permanentOptions'}>
                            <div className={'option duplicate'} onClick={(event) => {
                                duplicateElement(event, props.elementData);
                            }}><Duplicate isOption={true} /></div>
                            {elementData.name === 'Chart' && isStorydocAdmin && <CopyToClip elementData={elementData} />}
                            <div className={'option delete'} onClick={(event) => {
                                deleteElement(event);
                            }}><Delete isOption={true}/></div>
                        </div>
                    </div>
                </div>
            </div>
            </ElementDesignedEditor>,
            <div className={'addElement'} onClick={() =>  clickOpenElementsLibrary()}><Add/></div>
        ]
    }

    if(checkIfDisableElement()) {
        return (
            <Draggable draggableId={props.path?.slideIndex + '-' + props.path?.comboIndex + '-' + props.path?.setIndex + '-' + props.path?.groupIndex + '-' + props.path?.elementIndex + '-' + props.path?.subElementIndex}
                       index={props.index} key={props.index}>
                {(provided: any, snapshot: any) => (
                    <div className={'elementContainer ' + (isElementActive() ? 'active' : '')} ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
                        data-anchor-target={props.elementData.data.isAnchorActive ? props.elementData.data.anchorName : ''}>
                        {/*<div className={'draggingArea'} {...provided.dragHandleProps}></div>*/}
                        {elementContent()}
                    </div>
                )}
            </Draggable>
        )
    }
    else {
        return (
            <Draggable draggableId={props.path?.slideIndex + '-' + props.path?.comboIndex + '-' + props.path?.setIndex + '-' + props.path?.groupIndex + '-' + props.path?.elementIndex + '-' + props.path?.subElementIndex}
                       index={props.index} key={props.index}>
                {(provided: any, snapshot: any) => (
                    <div className={'elementContainer ' + (isElementActive() ? 'active' : '')} ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
                         data-anchor-target={props.elementData.data.isAnchorActive ? props.elementData.data.anchorName : ''}>
                        {elementContent()}
                    </div>
                )}
            </Draggable>
        );
    }
};

export default ElementEditor;
